import { useEffect, useState } from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";

const AddPhysicalCount = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { inventoryData, selectedOutlet, accessToken } = location.state || {};
    const token = sessionStorage.getItem('token');
    const [isLoading, setIsLoading] = useState(false);
    const [physicalItems, setPhysicalItems] = useState([]);

    useEffect(() => {
        if (accessToken !== "a7fK2pX9Zd") {
            navigate('/inventorycalculation');
        }
    }, [accessToken, navigate]);

    useEffect(() => {
        if (inventoryData?.summary) {
            const items = [];
            Object.values(inventoryData.summary).forEach(category => {
                Object.values(category).forEach(itemsArray => {
                    itemsArray.forEach(item => {
                        items.push({
                            id: `${item.name}-${item.uom}`,
                            item_name: item.name,
                            primaryQty: "0",
                            secondaryQty: "0",
                            uom: item.uom,
                            new_uom: item.new_uom,
                            unitdefinition: item.unitdefinition,
                            closing_balance: item.closing_balance
                        });
                    });
                });
            });
            setPhysicalItems(items);
        }
    }, [inventoryData]);

    const handlePrimaryChange = (id, value) => {
        setPhysicalItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? {
                    ...item,
                    primaryQty: value === "" ? "" : value
                } : item
            )
        );
    };

    const handleSecondaryChange = (id, value) => {
        setPhysicalItems(prevItems =>
            prevItems.map(item =>
                item.id === id ? {
                    ...item,
                    secondaryQty: value === "" ? "" : value
                } : item
            )
        );
    };

    function computeFinalQuantity(item, unitMappings) {
        const primaryQty = parseFloat(item.primaryQty) || 0;
        const secondaryQty = parseFloat(item.secondaryQty) || 0;
        const unitSize = parseFloat(item.unitdefinition.unit) || 1;
        const baseUom = (item.unitdefinition.uom || '').toLowerCase();
        const itemUom = (item.uom || '').toLowerCase();
        const newUom = (item.new_uom || '').toLowerCase();

        const {
            kgVariations,
            gramVariations,
            literVariations,
            milliliterVariations
        } = unitMappings;

        const isMatch = (uom, variations) => variations.includes(uom);

        let primaryInBaseUnit, secondaryInBaseUnit;

        if (itemUom === baseUom) {
            primaryInBaseUnit = primaryQty;
        } else {
            primaryInBaseUnit = primaryQty * unitSize;
        }

        if (newUom === baseUom) {
            secondaryInBaseUnit = secondaryQty;
        } else {
            if (isMatch(baseUom, kgVariations) && isMatch(newUom, gramVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else if (isMatch(baseUom, literVariations) && isMatch(newUom, milliliterVariations)) {
                secondaryInBaseUnit = secondaryQty / 1000;
            } else {
                secondaryInBaseUnit = secondaryQty;
            }
        }

        const totalInBaseUnit = primaryInBaseUnit + secondaryInBaseUnit;

        if (itemUom !== baseUom) {
            return (totalInBaseUnit / unitSize);
        }

        return totalInBaseUnit;
    }

    const unitMappings = {
        kgVariations: ['kg', 'kgs', 'kilogram', 'kilograms'],
        gramVariations: ['g', 'gram', 'grams', 'gm', 'gms', 'grm', 'grms'],
        literVariations: ['l', 'ltr', 'ltrs', 'liter', 'liters', 'litre', 'litres'],
        milliliterVariations: ['ml', 'milliliter', 'milliliters', 'millilitre', 'millilitres', 'mls']
    };

    const handleSubmit = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this physical count?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            if (!token || !selectedOutlet) {
                                toast.error("Please select an outlet");
                                return;
                            }

                            setIsLoading(true);
                            try {
                                const items = physicalItems.map(item => ({
                                    item_name: item.item_name,
                                    quantity: computeFinalQuantity(item, unitMappings)
                                }))

                                localStorage.setItem('savedPhysicalCount', JSON.stringify(items));
                                toast.success("Physical count saved!");
                                navigate('/inventorycalculation');
                            } catch (error) {
                                console.error('Error saving physical count locally:', error);
                                toast.error('Error saving physical count');
                            } finally {
                                setIsLoading(false);
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-physical-stocks'
            }
        );
    };

    const groupedItems = physicalItems.reduce((acc, item) => {
        const baseName = item.item_name;
        if (!acc[baseName]) {
            acc[baseName] = [];
        }
        acc[baseName].push(item);
        return acc;
    }, {});

    const shouldShowSecondary = (item) => {
        const itemUom = item.uom.toLowerCase().trim();
        const newUom = item.new_uom.toLowerCase().trim();
        const defUom = (item.unitdefinition.uom || '').toLowerCase().trim();

        if (!defUom) {
            return itemUom !== newUom;
        }

        return !(itemUom === newUom && newUom === defUom);
    };

    return (
        <div className="flex flex-col min-h-screen">
            <Navbar />

            <div className="flex-grow">
                <div className="max-w-6xl my-5 mx-auto">
                    {token && selectedOutlet && (
                        <div className="bg-white rounded-lg shadow-md p-6">
                            <h2 className="text-xl font-semibold mb-4">Physical Stock Count</h2>

                            <div className="rounded-md border overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 text-sm text-center">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Definition</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Primary Unit</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider" colSpan="2">Secondary Unit</th>
                                        </tr>
                                        <tr>
                                            <th></th>
                                            <th></th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-r">UOM</th>
                                            <th className="px-6 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {Object.entries(groupedItems).map(([itemName, items]) => {
                                            const item = items[0];
                                            const showSecondary = shouldShowSecondary(item);

                                            return (
                                                <tr key={itemName}>
                                                    <td className="px-6 py-4 whitespace-nowrap">{itemName}</td>
                                                    <td className="px-6 py-4 whitespace-nowrap">{item.unitdefinition?.unit || 0} ({item.unitdefinition?.uom || 'N/A'})</td>

                                                    <td className="px-6 py-4 whitespace-nowrap border-r">{item.uom}</td>
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className="w-20 border rounded px-2 py-1"
                                                            value={item.primaryQty}
                                                            onChange={(e) => handlePrimaryChange(item.id, e.target.value)}
                                                            step="any"
                                                            min="0"
                                                        />
                                                    </td>

                                                    <td className="px-6 py-4 whitespace-nowrap border-r">
                                                        {showSecondary ? item.new_uom : '-'}
                                                    </td>
                                                    <td>
                                                        {showSecondary ? (
                                                            <input
                                                                type="number"
                                                                className="w-20 border rounded px-2 py-1"
                                                                value={item.secondaryQty}
                                                                onChange={(e) => handleSecondaryChange(item.id, e.target.value)}
                                                                step="any"
                                                                min="0"
                                                            />
                                                        ) : (
                                                            '-'
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex justify-end mt-3">
                                <button
                                    onClick={handleSubmit}
                                    disabled={isLoading}
                                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400"
                                >
                                    {isLoading ? 'Saving...' : 'Save Physical Count'}
                                </button>
                            </div>

                        </div>
                    )}
                </div>
            </div>

            <Footer />
        </div >
    );
};

export default AddPhysicalCount;